import React, { useEffect, useState } from "react";
import {
  Printer,
  Check,
  FileText,
  Tag,
  Usb,
  Unplug,
  Download,
  HelpCircle,
  ExternalLink,
  X,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Terminal,
  Inbox,
} from "lucide-react";
import { ORIENTATIONS, THERMAL_WIDTHS } from "../utils/receiptConfig";
import ThermalPrinter from "./ThermalPrinter";
import SplitReceiptConfig from "./SplitReceiptConfig";
import LabelPrinterConfig from "./LabelPrinterConfig";
import {
  isWebUSBSupported,
  requestUsbPrinter,
  getSavedUsbPrinter,
  disconnectUsbPrinter,
} from "../utils/usbPrintManager";
import {
  isWebSerialSupported,
  requestSerialPrinter,
  getSavedSerialPrinter,
  disconnectSerialPrinter,
} from "../utils/serialPrintManager";
import {
  getSavedWindowsPrinter,
  setSavedWindowsPrinter,
  disconnectWindowsPrinter,
  isWindowsPrintBridgeAvailable,
  listWindowsPrinters,
} from "../utils/windowsPrintBridge";
import { executeOpenCashDrawer } from "../utils/printManager";

// Link Download Helper ZIP ASAlink-Printer-Bridge.zip
const HELPER_DOWNLOAD_URL = "https://drive.google.com/file/d/1DVEGq8lQZ8kTXHUEf2SW5WPPKj0oN0rz/view?usp=sharing";

export default function PrinterConfigTab({
  config,
  onChange,
  footerNote = "",
  onFooterNoteChange,
  storeName = "",
  storeAddress = "",
  storePhone = "",
  storeLogoUrl = "",
}) {
  const isThermal = config.orientation === "thermal";
  const isKuliner = localStorage.getItem("PREVIEW_APPS") === "true";
  const [usbPrinter, setUsbPrinter] = useState(() => getSavedUsbPrinter());
  const [usbBusy, setUsbBusy] = useState(false);
  const [usbMessage, setUsbMessage] = useState("");
  const [serialPrinter, setSerialPrinter] = useState(() => getSavedSerialPrinter());
  const [serialBusy, setSerialBusy] = useState(false);
  const [serialMessage, setSerialMessage] = useState("");
  const [windowsPrinter, setWindowsPrinter] = useState(() => getSavedWindowsPrinter());
  const [windowsPrinters, setWindowsPrinters] = useState([]);
  const [windowsBusy, setWindowsBusy] = useState(false);
  const [windowsMessage, setWindowsMessage] = useState("");
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [checkingBridge, setCheckingBridge] = useState(false);
  const [bridgeLiveStatus, setBridgeLiveStatus] = useState(null);
  const [cashDrawerTesting, setCashDrawerTesting] = useState(false);
  const [cashDrawerMessage, setCashDrawerMessage] = useState("");

  const handleTestCashDrawer = async () => {
    setCashDrawerTesting(true);
    setCashDrawerMessage("");
    try {
      await executeOpenCashDrawer();
      setCashDrawerMessage("Perintah buka laci berhasil dikirim ke printer.");
    } catch (err) {
      setCashDrawerMessage(err?.message || "Gagal membuka laci kasir. Pastikan printer terhubung.");
    } finally {
      setCashDrawerTesting(false);
    }
  };

  const handleToggleAutoOpenCashDrawer = () => {
    updateField({ autoOpenCashDrawer: !config.autoOpenCashDrawer });
  };

  const checkLiveBridgeStatus = async () => {
    setCheckingBridge(true);
    try {
      const ok = await isWindowsPrintBridgeAvailable();
      setBridgeLiveStatus(ok ? "online" : "offline");
    } catch {
      setBridgeLiveStatus("offline");
    } finally {
      setCheckingBridge(false);
    }
  };

  const handleConnectUsb = async () => {
    setUsbBusy(true);
    setUsbMessage("");
    try {
      const saved = await requestUsbPrinter();
      disconnectWindowsPrinter();
      await disconnectSerialPrinter();
      setWindowsPrinter(null);
      setSerialPrinter(null);
      setUsbPrinter(saved);
      setUsbMessage("Printer USB siap. Cetak akan memakai ESC POS langsung.");
    } catch (error) {
      setUsbMessage(error?.message || "Printer USB belum berhasil disambungkan.");
    } finally {
      setUsbBusy(false);
    }
  };

  const handleDisconnectUsb = async () => {
    await disconnectUsbPrinter();
    setUsbPrinter(null);
    setUsbMessage("Printer USB dilepas dari perangkat ini.");
  };

  const handleConnectSerial = async () => {
    setSerialBusy(true);
    setSerialMessage("");
    try {
      const saved = await requestSerialPrinter({ baudRate: 9600 });
      // Jangan biarkan koneksi lain mengambil prioritas atas port COM.
      await disconnectUsbPrinter();
      disconnectWindowsPrinter();
      setUsbPrinter(null);
      setWindowsPrinter(null);
      setSerialPrinter(saved);
      setSerialMessage("Port COM siap. Cetak akan memakai ESC POS langsung.");
    } catch (error) {
      setSerialMessage(error?.message || "Port COM belum berhasil disambungkan.");
    } finally {
      setSerialBusy(false);
    }
  };

  const handleDisconnectSerial = async () => {
    await disconnectSerialPrinter();
    setSerialPrinter(null);
    setSerialMessage("Port COM dilepas dari perangkat ini.");
  };

  useEffect(() => {
    let cancelled = false;
    isWindowsPrintBridgeAvailable().then(async (available) => {
      if (cancelled) return;
      if (!available && windowsPrinter) {
        setWindowsMessage("Helper Windows belum berjalan di komputer ini.");
      } else if (available && !windowsPrinter) {
        try {
          const printers = await listWindowsPrinters();
          if (!cancelled && printers && printers.length > 0) {
            setWindowsPrinters(printers);
            handleSelectWindowsPrinter(printers[0]);
          }
        } catch (_) {}
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFindWindowsPrinters = async () => {
    setWindowsBusy(true);
    setWindowsMessage("");
    try {
      const available = await isWindowsPrintBridgeAvailable();
      if (!available) {
        throw new Error("Helper Windows belum berjalan. Jalankan printer bridge terlebih dahulu.");
      }
      const printers = await listWindowsPrinters();
      setWindowsPrinters(printers);
      if (printers.length === 0) {
        setWindowsMessage("Tidak ada printer Windows yang terdeteksi.");
      } else if (!windowsPrinter) {
        handleSelectWindowsPrinter(printers[0]);
      }
    } catch (error) {
      setWindowsMessage(error?.message || "Gagal membaca daftar printer Windows.");
    } finally {
      setWindowsBusy(false);
    }
  };

  const handleSelectWindowsPrinter = async (name) => {
    const saved = setSavedWindowsPrinter(name);
    await disconnectSerialPrinter();
    await disconnectUsbPrinter();
    setSerialPrinter(null);
    setUsbPrinter(null);
    setWindowsPrinter(saved);
    setWindowsMessage(saved ? "Printer Windows siap menerima raw ESC/POS." : "Printer Windows dilepas.");
  };

  const handleDisconnectWindowsPrinter = () => {
    disconnectWindowsPrinter();
    setWindowsPrinter(null);
    setWindowsMessage("Printer Windows dilepas dari perangkat ini.");
  };

  const updateField = (patch) => {
    if (onChange) onChange({ ...config, ...patch });
  };

  const renderWindowsSpoolerCard = () => (
    <div className="rounded-xl bg-amber-50/70 ring-1 ring-amber-200/80 p-4">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${windowsPrinter ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {windowsPrinter ? <Check className="w-4 h-4" /> : <Usb className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            Printer Windows USB Spooler {!isThermal && "(Direkomendasikan untuk Dot Matrix / A5)"}
          </p>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
            {isThermal
              ? "Untuk printer USB003 yang memakai driver POS 80 mm. Helper lokal mengirim raw ESC/POS melalui Windows Spooler tanpa mengganti driver."
              : "Sangat direkomendasikan untuk printer Windows (Dot Matrix LX-300 / LX-310, POS 80 mm, atau printer biasa). Helper lokal mengirim data teks dan tabel nota A5 langsung ke printer Windows Spooler agar hasil cetak tajam bebas blur."}
          </p>
          {windowsPrinter && (
            <p className="text-xs font-medium text-emerald-700 mt-2 truncate">
              Siap memakai {windowsPrinter.name}
            </p>
          )}
          {windowsMessage && <p className="text-xs text-amber-800 mt-2">{windowsMessage}</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-amber-200/60">
        <button
          type="button"
          onClick={handleFindWindowsPrinters}
          disabled={windowsBusy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-60 transition-colors shadow-sm"
        >
          <Printer className="w-3.5 h-3.5" />
          {windowsBusy ? "Mencari..." : "Cari Printer Windows"}
        </button>

        <a
          href={HELPER_DOWNLOAD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white text-amber-900 ring-1 ring-amber-300 text-xs font-semibold hover:bg-amber-100/80 transition-colors shadow-sm"
        >
          <Download className="w-3.5 h-3.5 text-amber-700" />
          Download Helper (ZIP)
        </a>

        <button
          type="button"
          onClick={() => {
            setShowTutorialModal(true);
            checkLiveBridgeStatus();
          }}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-100/90 text-amber-900 text-xs font-semibold hover:bg-amber-200 transition-colors"
        >
          <HelpCircle className="w-3.5 h-3.5 text-amber-700" />
          Petunjuk Instalasi
        </button>
        
        {windowsPrinter && (
          <button
            type="button"
            onClick={handleDisconnectWindowsPrinter}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-slate-700 ring-1 ring-slate-200 text-xs font-semibold hover:bg-slate-100"
          >
            Lepas
          </button>
        )}
      </div>
      {windowsPrinters.length > 0 && (
        <div className="mt-3 space-y-2">
          {windowsPrinters.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => handleSelectWindowsPrinter(name)}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs ring-1 transition-colors ${windowsPrinter?.name === name ? "bg-emerald-50 text-emerald-800 ring-emerald-300" : "bg-white text-slate-700 ring-slate-200 hover:bg-amber-100/60"}`}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      {/* === Settings === */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-6 lg:col-span-3">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-50 ring-1 ring-teal-200/70 flex items-center justify-center">
            <Printer className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 tracking-tight">
              Konfigurasi Printer Nota
            </h3>
            <p className="text-xs text-slate-500">
              Atur orientasi, ukuran kertas, dan footer nota saat dicetak.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Orientation */}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-3">
              Orientasi Nota
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ORIENTATIONS.map((opt) => {
                const active = config.orientation === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateField({ orientation: opt.value })}
                    className={`relative text-left p-4 rounded-xl ring-1 transition-colors ${
                      active
                        ? "bg-teal-50/60 ring-teal-300 text-teal-900"
                        : "bg-white ring-slate-200 text-slate-700 hover:bg-slate-50"
                    }`}>
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-0.5 w-5 h-5 rounded-full ring-2 flex items-center justify-center ${active ? "ring-teal-600 bg-teal-600" : "ring-slate-300 bg-white"}`}>
                        {active && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <p
                          className={`text-sm font-semibold ${active ? "text-teal-900" : "text-slate-900"}`}>
                          {opt.label}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {opt.value === "horizontal"
                            ? "Cocok untuk printer biasa dengan kertas A5 landscape."
                            : "Cocok untuk printer struk thermal (POS)."}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Thermal width & Order Receipt Config Row */}
          {isThermal && (
            <div className={`grid grid-cols-1 ${isKuliner ? "md:grid-cols-2" : "md:grid-cols-1"} gap-6`}>
              {/* Thermal width */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-3">
                  Ukuran Kertas Thermal
                </label>
                <div className="flex gap-3">
                  {THERMAL_WIDTHS.map((w) => {
                    const active = config.thermalWidth === w;
                    return (
                      <button
                        key={w}
                        type="button"
                        onClick={() => updateField({ thermalWidth: w })}
                        className={`px-5 py-2.5 rounded-lg ring-1 text-sm font-semibold transition-colors ${
                          active
                            ? "bg-teal-600 text-white ring-teal-600 shadow-sm shadow-teal-600/20"
                            : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                        }`}>
                        {w}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Pilih sesuai lebar fisik kertas roll printer thermal Anda.
                </p>
              </div>

              {/* Konfigurasi Nota Pesanan — Kuliner Only */}
              {isKuliner && (
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-3">
                    Konfigurasi Nota Pesanan (Order)
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => updateField({ orderReceiptEnabled: false })}
                      className={`px-5 py-2.5 rounded-lg ring-1 text-sm font-semibold transition-colors ${
                        !config.orderReceiptEnabled
                          ? "bg-teal-600 text-white ring-teal-600 shadow-sm shadow-teal-600/20"
                          : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"
                      }`}>
                      Non-Aktif
                    </button>
                    <button
                      type="button"
                      onClick={() => updateField({ orderReceiptEnabled: true })}
                      className={`px-5 py-2.5 rounded-lg ring-1 text-sm font-semibold transition-colors ${
                        config.orderReceiptEnabled
                          ? "bg-teal-600 text-white ring-teal-600 shadow-sm shadow-teal-600/20"
                          : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"
                      }`}>
                      Aktif
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Aktifkan untuk mencetak nota pesanan (order) dari sistem.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Footer Nota (B.11 — moved from Konfigurasi Toko) */}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-3">
              Footer Nota
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
              <textarea
                value={footerNote}
                onChange={(e) =>
                  onFooterNoteChange && onFooterNoteChange(e.target.value)
                }
                rows={3}
                placeholder="Contoh: Terima Kasih Telah Menjadi Pelanggan Kami"
                className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors resize-none"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Teks ini akan muncul di bagian bawah setiap nota cetak.
            </p>
          </div>

          {/* Note */}
          <div className="bg-sky-50/60 ring-1 ring-sky-200/70 rounded-xl p-4">
            <p className="text-xs text-sky-800 leading-relaxed">
              <strong>Catatan:</strong> Orientasi dan ukuran kertas disimpan
              lokal pada perangkat ini. Footer nota disimpan ke data toko.
            </p>
          </div>

          {/* Laci Kasir (Cash Drawer RJ11) */}
          <div className="rounded-xl bg-slate-50/80 ring-1 ring-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${config.autoOpenCashDrawer ? "bg-teal-100 text-teal-700" : "bg-slate-200 text-slate-500"}`}>
                  <Inbox className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">
                      Buka Laci Kasir Otomatis (RJ11)
                    </p>
                    {config.autoOpenCashDrawer && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-teal-100 text-teal-800">
                        Aktif
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Kirim sinyal pembuka laci kasir (pulsa RJ11 Pin 2 & Pin 5) ke printer kasir secara otomatis saat nota pembayaran transaksi dicetak.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleToggleAutoOpenCashDrawer}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  config.autoOpenCashDrawer ? "bg-teal-600" : "bg-slate-200"
                }`}>
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    config.autoOpenCashDrawer ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-200/80">
              <p className="text-[11px] text-slate-400">
                Hubungkan kabel RJ11 dari laci kasir ke port DK (Drawer Kick) di belakang printer struk Anda.
              </p>
              <button
                type="button"
                onClick={handleTestCashDrawer}
                disabled={cashDrawerTesting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-slate-700 ring-1 ring-slate-300 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60 transition-colors shadow-sm shrink-0"
              >
                {cashDrawerTesting ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-teal-600" />
                ) : (
                  <Inbox className="w-3.5 h-3.5 text-teal-600" />
                )}
                {cashDrawerTesting ? "Mengirim sinyal..." : "Uji Coba Buka Laci"}
              </button>
            </div>

            {cashDrawerMessage && (
              <p className={`text-xs mt-2 font-medium ${cashDrawerMessage.includes("berhasil") ? "text-emerald-700" : "text-rose-600"}`}>
                {cashDrawerMessage}
              </p>
            )}
          </div>

          {/* Direct hardware printing: USB, COM, Windows Spooler, and Android Bluetooth (Khusus Thermal) */}
          {isThermal ? (
            <div className="pt-6 mt-6 border-t border-slate-200 space-y-4">
              <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${usbPrinter ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                    {usbPrinter ? <Check className="w-4 h-4" /> : <Usb className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">Cetak langsung USB</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Mengirim nota sebagai ESC POS mentah agar hasil Windows tajam seperti APK. Gunakan Chrome dan sambungkan printer USB sebelum mencetak.
                    </p>
                    {usbPrinter && (
                      <p className="text-xs font-medium text-emerald-700 mt-2 truncate">
                        Siap memakai {usbPrinter.productName || "Printer USB"}
                      </p>
                    )}
                    {usbMessage && (
                      <p className={`text-xs mt-2 ${usbPrinter ? "text-emerald-700" : "text-amber-700"}`}>
                        {usbMessage}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {isWebUSBSupported() ? (
                    <>
                      <button
                        type="button"
                        onClick={handleConnectUsb}
                        disabled={usbBusy}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700 disabled:opacity-60"
                      >
                        <Usb className="w-3.5 h-3.5" />
                        {usbBusy ? "Menyambungkan..." : usbPrinter ? "Ganti Printer USB" : "Sambungkan Printer USB"}
                      </button>
                      {usbPrinter && (
                        <button
                          type="button"
                          onClick={handleDisconnectUsb}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-slate-600 ring-1 ring-slate-200 text-xs font-semibold hover:bg-slate-100"
                        >
                          <Unplug className="w-3.5 h-3.5" />
                          Lepas
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-amber-700">WebUSB tidak tersedia. Gunakan Chrome versi terbaru melalui HTTPS atau localhost.</p>
                  )}
                </div>
              </div>

              <div className="rounded-xl bg-indigo-50/60 ring-1 ring-indigo-200/70 p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${serialPrinter ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-600"}`}>
                    {serialPrinter ? <Check className="w-4 h-4" /> : <Usb className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">Printer Bluetooth melalui COM</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Untuk printer yang muncul sebagai COM4 atau port COM lain di Windows. Data dikirim langsung sebagai ESC POS tanpa preview browser.
                    </p>
                    {serialPrinter && (
                      <p className="text-xs font-medium text-emerald-700 mt-2 truncate">
                        Siap memakai {serialPrinter.name || "Printer COM"} pada baud rate {serialPrinter.baudRate}
                      </p>
                    )}
                    {serialMessage && <p className="text-xs text-indigo-700 mt-2">{serialMessage}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {isWebSerialSupported() ? (
                    <>
                      <button
                        type="button"
                        onClick={handleConnectSerial}
                        disabled={serialBusy}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-60"
                      >
                        <Usb className="w-3.5 h-3.5" />
                        {serialBusy ? "Menyambungkan..." : serialPrinter ? "Ganti Port COM" : "Sambungkan Port COM"}
                      </button>
                      {serialPrinter && (
                        <button
                          type="button"
                          onClick={handleDisconnectSerial}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-slate-600 ring-1 ring-slate-200 text-xs font-semibold hover:bg-slate-100"
                        >
                          <Unplug className="w-3.5 h-3.5" />
                          Lepas
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-amber-700">Web Serial tidak tersedia. Gunakan Chrome terbaru melalui localhost atau HTTPS.</p>
                  )}
                </div>
              </div>

              {/* Printer Windows USB Spooler */}
              {renderWindowsSpoolerCard()}

              {/* Thermal Printer Connect (Khusus Android Bluetooth) */}
              <ThermalPrinter 
                storeName={storeName}
                storeAddress={storeAddress}
                storePhone={storePhone}
                storeLogoUrl={storeLogoUrl}
                footerNote={footerNote}
              />
            </div>
          ) : (
            <div className="pt-6 mt-6 border-t border-slate-200 space-y-4">
              {renderWindowsSpoolerCard()}

              <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-teal-100 text-teal-700 shrink-0">
                    <Printer className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">Alternatif Dialog Cetak Browser (Windows Popup Print)</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Jika Helper Windows Spooler tidak aktif atau belum memilih printer di atas, sistem otomatis beralih ke jendela dialog browser saat mencetak nota.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* === Preview === (B.12) */}
      <div className="lg:col-span-2">
        <ReceiptPreview 
          config={config} 
          footerNote={footerNote} 
          storeName={storeName} 
          storeAddress={storeAddress}
          storePhone={storePhone}
          storeLogoUrl={storeLogoUrl}
          onChange={updateField} 
        />
        
        {isKuliner && config.orientation === "thermal" && (
          <SplitReceiptConfig config={config} onChange={updateField} />
        )}

        {/* Label Pesanan Config — Kuliner Only */}
        {isKuliner && config.orientation === "thermal" && (
          <LabelPrinterConfig config={config} onChange={updateField} />
        )}
      </div>
    
      {/* Modal Petunjuk Instalasi Helper Windows */}
      {showTutorialModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-amber-500 text-white shadow-sm">
                  <Terminal className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 leading-tight">
                    Petunjuk Instalasi Helper Printer Windows
                  </h3>
                  <p className="text-xs text-slate-500">
                    ASAlink Printer Bridge untuk Cetak Struk & Barcode 1-Klik
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowTutorialModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-4 text-slate-700 text-xs">
              {/* Status Helper Live */}
              <div className="flex items-center justify-between p-3.5 rounded-xl border border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2.5">
                  <span className="font-semibold text-slate-800">Status Helper di Komputer Ini:</span>
                  {checkingBridge ? (
                    <span className="inline-flex items-center gap-1 text-slate-500 text-[11px]">
                      <RefreshCw className="w-3 h-3 animate-spin" /> Memeriksa...
                    </span>
                  ) : bridgeLiveStatus === "online" ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Terhubung & Aktif
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-600" /> Belum Berjalan
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={checkLiveBridgeStatus}
                  disabled={checkingBridge}
                  className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 font-semibold text-[11px] flex items-center gap-1 transition-colors"
                >
                  <RefreshCw className={`w-3 h-3 ${checkingBridge ? "animate-spin" : ""}`} />
                  Cek Ulang
                </button>
              </div>

              {/* Langkah-langkah */}
              <div className="space-y-3">
                <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-1.5">
                  <div className="flex items-center gap-2 font-bold text-slate-900 text-xs">
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px]">1</span>
                    <span>Download File ZIP Helper</span>
                  </div>
                  <p className="text-slate-600 text-[11px] pl-7 leading-relaxed">
                    Download file ZIP <strong>ASAlink-Printer-Bridge.zip</strong> ke komputer kasir Anda.
                  </p>
                  <div className="pl-7 pt-1">
                    <a
                      href={HELPER_DOWNLOAD_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold text-[11px] transition-colors shadow-sm"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download File ZIP Helper
                    </a>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-1.5">
                  <div className="flex items-center gap-2 font-bold text-slate-900 text-xs">
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px]">2</span>
                    <span>Ekstrak ZIP & Jalankan install.bat</span>
                  </div>
                  <p className="text-slate-600 text-[11px] pl-7 leading-relaxed">
                    Ekstrak file zip yang sudah didownload. Buka foldernya lalu klik ganda (double-click) pada file <strong>install.bat</strong>.
                  </p>
                  <p className="text-[11px] text-slate-500 pl-7 italic">
                    *Jika muncul jendela biru Windows SmartScreen, klik <strong>More info</strong> lalu pilih <strong>Run anyway</strong>.
                  </p>
                </div>

                <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-1.5">
                  <div className="flex items-center gap-2 font-bold text-slate-900 text-xs">
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px]">3</span>
                    <span>Otomatis Aktif & Berjalan Saat Windows Menyala</span>
                  </div>
                  <p className="text-slate-600 text-[11px] pl-7 leading-relaxed">
                    Skrip akan memasang helper ke startup Windows. Anda <strong>tidak perlu</strong> menjalankan file ini lagi setiap komputer dinyalakan.
                  </p>
                </div>

                <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-1.5">
                  <div className="flex items-center gap-2 font-bold text-slate-900 text-xs">
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px]">4</span>
                    <span>Pilih Printer di Menu Ini</span>
                  </div>
                  <p className="text-slate-600 text-[11px] pl-7 leading-relaxed">
                    Tutup petunjuk ini, lalu klik tombol <strong>"Cari Printer Windows"</strong> dan pilih nama printer Anda (misal: <em>POS-80</em> atau <em>Xprinter XP-420B</em>).
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <span className="text-[11px] text-slate-400">
                Port Local: 127.0.0.1:17890
              </span>
              <button
                type="button"
                onClick={() => setShowTutorialModal(false)}
                className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition-colors"
              >
                Saya Mengerti
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function ReceiptPreview({ 
  config, 
  footerNote, 
  storeName = "", 
  storeAddress = "", 
  storePhone = "", 
  storeLogoUrl = "", 
  onChange 
}) {
  const isThermal = config.orientation === "thermal";
  const thermalWidth = config.thermalWidth || "80mm";
  const labelPrinterWidth = config.labelPrinterWidth || "58mm";
  const [activePreviewTab, setActivePreviewTab] = React.useState("bayar");
  const isKuliner = localStorage.getItem("PREVIEW_APPS") === "true";

  // Load dynamic store data with fallback
  const displayStoreName = storeName || localStorage.getItem('tenantName') || 'ASA LINK';
  const displayStoreAddress = storeAddress || localStorage.getItem('tenantAddress') || 'Jl. Contoh No. 1';
  const displayStorePhone = storePhone || localStorage.getItem('tenantPhone') || '081234567890';
  const displayStoreLogoUrl = storeLogoUrl || localStorage.getItem('tenantLogoUrl') || '';

  React.useEffect(() => {
    if (!isThermal) {
      setActivePreviewTab("bayar");
    }
  }, [isThermal]);

  const handleToggleAutoPrint = () => {
    if (onChange) {
      onChange({ autoPrintEnabled: !config.autoPrintEnabled });
    }
  };

  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-50 ring-1 ring-amber-200/70 flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-900">Preview Nota</h4>
            <p className="text-[11px] text-slate-500">
              {activePreviewTab === "pesanan"
                ? `Thermal · ${thermalWidth}`
                : isThermal
                  ? `Thermal · ${thermalWidth}`
                  : "Horizontal · A5 Landscape"}
            </p>
          </div>
        </div>

        {/* Auto Print Switch */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <span className="text-xs font-semibold text-slate-700 block">Auto Print Nota</span>
            <span className="text-[10px] text-slate-400 block leading-tight max-w-[180px]">
              Cetak otomatis setelah transaksi berhasil
            </span>
          </div>
          <button
            type="button"
            onClick={handleToggleAutoPrint}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
              config.autoPrintEnabled ? "bg-teal-600" : "bg-slate-200"
            }`}>
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                config.autoPrintEnabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>
      </div>

      {isThermal && (
        <div className="flex border border-slate-300 divide-x divide-slate-300 rounded-lg mb-4 max-w-[320px] mx-auto overflow-hidden">
          <button
            type="button"
            onClick={() => setActivePreviewTab("bayar")}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-none transition-all ${
              activePreviewTab === "bayar"
                ? "bg-teal-600 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-800 bg-white"
            }`}>
            Nota Bayar
          </button>
          {isKuliner && (
            <button
              type="button"
              onClick={() => setActivePreviewTab("pesanan")}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-none transition-all ${
                activePreviewTab === "pesanan"
                  ? "bg-teal-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-800 bg-white"
              }`}>
              Nota Pesanan
            </button>
          )}
          {isKuliner && (
            <button
              type="button"
              onClick={() => setActivePreviewTab("label")}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-none transition-all ${
                activePreviewTab === "label"
                  ? "bg-teal-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-800 bg-white"
              }`}>
              Label
            </button>
          )}
        </div>
      )}

      <div className="bg-slate-100/70 rounded-xl p-4 flex items-center justify-center min-h-[280px]">
        {activePreviewTab === "label" ? (
          // Label Pesanan preview — compact stiker format
          <div
            className="bg-white shadow-md rounded-sm font-mono text-[9px] leading-relaxed text-slate-800 p-3.5 flex flex-col justify-between"
            style={{ width: labelPrinterWidth === "58mm" ? 160 : 220, minHeight: 120 }}>
            <div>
              {/* Row 1: Nama Toko | Tipe Penjualan */}
              <div className="flex justify-between items-baseline text-[8px] text-slate-500 mb-1">
                <span>{displayStoreName}</span>
                <span>Take Away</span>
              </div>
              {/* Row 2: Nama Pelanggan */}
              <div className="mb-0.5">
                <span className="font-bold text-[12px] text-slate-900">Vina Ananta</span>
              </div>
              {/* Qty + Unit (Porsi) */}
              <div className="text-[9px] text-slate-700 mb-0.5 font-semibold">1 Cup</div>
              {/* Nama Produk + Varian (dalam kurung) */}
              <div className="font-bold text-[11px] text-slate-900 mb-0.5 leading-snug">
                Ayam Geprek (Pedas)
              </div>
              {/* Addon */}
              <div className="text-[8.5px] text-slate-500 mb-1">+ Extra Keju</div>
              {/* Catatan item real: setelah addons, memakai bintang tanpa kata "Catatan:" */}
              <div className="text-[8.5px] text-slate-600 mb-1 font-semibold">* Sambal dipisah</div>
            </div>
            {/* Datetime */}
            <div className="pt-2.5">
              <div className="flex justify-between items-baseline text-[8px] text-slate-500 pt-1.5">
                <span>25/06/26 13:56</span>
                <span className="text-[9px] font-semibold text-slate-800">1/3</span>
              </div>
            </div>
          </div>
        ) : activePreviewTab === "pesanan" ? (
          // Nota Pesanan (Order) preview — EXACT match to actual order receipt
          <div
            className="bg-white shadow-md rounded-sm font-mono text-[9px] leading-tight text-slate-800 p-3"
            style={{
              width: thermalWidth === "58mm" ? 140 : 180,
            }}>
            <div className="text-center font-bold text-[11px] uppercase tracking-wide mb-0.5">
              Nota Pesanan
            </div>

            <div className="border-t border-dashed border-slate-300 my-1.5" />
            
            <div className="space-y-0.5 text-[8px]">
              <div className="flex justify-between font-bold">
                <span>No. TRX-004</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Kasir:</span>
                <span>Budi</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Pelanggan:</span>
                <span>Pelanggan Umum</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Waktu:</span>
                <span>25 Jan 2026, 15.55</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Tipe Penjualan:</span>
                <span>Regular</span>
              </div>
              <div className="flex justify-between font-bold text-slate-900 text-[9px]">
                <span>MEJA:</span>
                <span>Meja 04</span>
              </div>
            </div>

            <div className="border-t border-dashed border-slate-300 my-1.5" />

            {/* Product items (single line spacing, with notes) */}
            <div className="space-y-1 text-[8.5px]">
              <div>
                <div className="font-bold text-slate-900">1x Bad Memories Mocktail</div>
                <div className="text-[7.5px] italic text-amber-700 font-semibold">* Less Sugar</div>
                <div className="text-[7.5px] italic text-slate-600 pl-1.5">+ Extra Jelly (x1)</div>
              </div>
              <div>
                <div className="font-bold text-slate-900">1x Butterscotch Memo (Iced)</div>
              </div>
            </div>

          </div>
        ) : isThermal ? (
          // Thermal/vertical preview (narrow strip) — EXACT match to actual transaction receipt
          <div
            className="bg-white shadow-md rounded-sm font-mono text-[9px] leading-tight text-slate-800 p-3"
            style={{
              width: thermalWidth === "58mm" ? 140 : 180,
              minHeight: 260,
            }}>
            {displayStoreLogoUrl && config.printLogo !== false && (
              <div className="flex justify-center mb-1.5">
                <img src={displayStoreLogoUrl} alt="Logo" className="w-8 max-h-8 object-contain" />
              </div>
            )}
            <div className="text-center font-bold text-[10px] mb-0.5">{displayStoreName}</div>
            <div className="text-center text-[7.5px] text-slate-500 leading-tight">{displayStoreAddress}</div>
            <div className="text-center text-[7.5px] text-slate-500 mb-1">{displayStorePhone}</div>
            
            <div className="border-t border-dashed border-slate-300 my-1.5" />
            
            <div className="space-y-0.5 text-[8px]">
              <div className="flex justify-between font-semibold text-slate-900">
                <span>TRX-004</span>
                <span className="text-right">24 Jun 2026, 14.50 WIB</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Pelanggan:</span>
                <span className="text-right font-semibold text-slate-800">Pelanggan Umum</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Tipe Penjualan:</span>
                <span className="text-right">Regular</span>
              </div>
              {isKuliner && (
                <div className="flex justify-between font-bold text-slate-900">
                  <span>Meja:</span>
                  <span className="text-right">Meja 04</span>
                </div>
              )}
              <div className="flex justify-between text-slate-600">
                <span>Kasir:</span>
                <span className="text-right">Glazzie</span>
              </div>
            </div>
            
            <div className="border-t border-dashed border-slate-300 my-1.5" />
            
            {/* Daftar Produk 1 Spasi */}
            <div className="space-y-1 text-[8.5px]">
              {isKuliner ? (
                <>
                  <div>
                    <div className="font-semibold text-slate-900">Caramel Memo (Iced)</div>
                    <div className="flex justify-between text-[7.5px] text-slate-600">
                      <span>1 X Rp 19.000</span>
                      <span>Rp 19.000</span>
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">Choco Caramel (Frappe)</div>
                    <div className="flex justify-between text-[7.5px] text-slate-600">
                      <span>1 X Rp 22.000</span>
                      <span>Rp 22.000</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div className="font-semibold text-slate-900">Kemeja Polos Premium (L)</div>
                    <div className="text-[7.5px] italic text-amber-700 font-semibold">* Warna Biru Dongker</div>
                    <div className="flex justify-between text-[7.5px] text-slate-600">
                      <span>1 X Rp 125.000</span>
                      <span>Rp 125.000</span>
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">Celana Chino Slim Fit (32)</div>
                    <div className="flex justify-between text-[7.5px] text-slate-600">
                      <span>1 X Rp 175.000</span>
                      <span>Rp 175.000</span>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="border-t border-dashed border-slate-300 my-1.5" />
            
            <div className="space-y-0.5 text-[8px]">
              <div className="flex justify-between">
                <span>Sub Total</span>
                <span>{isKuliner ? "Rp 41.000" : "Rp 300.000"}</span>
              </div>
              <div className="flex justify-between font-bold text-slate-900">
                <span>Total Tagihan</span>
                <span>{isKuliner ? "Rp 41.000" : "Rp 300.000"}</span>
              </div>
              <div className="flex justify-between">
                <span>Bayar (Tunai)</span>
                <span>{isKuliner ? "Rp 60.000" : "Rp 300.000"}</span>
              </div>
              <div className="flex justify-between">
                <span>Kembalian</span>
                <span>{isKuliner ? "Rp 19.000" : "Rp 0"}</span>
              </div>
            </div>
            
            <div className="border-t border-dashed border-slate-300 my-1.5" />
            
            <div className="text-center text-[7.5px] leading-tight px-1 mt-1 text-slate-600">
              {footerNote || "Terima Kasih Telah Menjadi Pelanggan Kami"}
            </div>
            <div className="text-center text-[7px] text-slate-400 mt-1">
              Powered by ASAlink
            </div>
          </div>
        ) : (
          // Horizontal (A5 Landscape) preview — invoice-style 2 columns
          <div
            className="bg-white shadow-md rounded-sm text-[8px] leading-tight text-slate-700 p-3"
            style={{ width: 420, minHeight: "auto", fontFamily: "Arial, sans-serif" }}>
            {/* Header: store + customer */}
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div className="flex gap-2 items-start">
                {config.printLogo !== false && (
                  displayStoreLogoUrl ? (
                    <img src={displayStoreLogoUrl} alt="Logo" className="w-7 h-7 object-contain rounded-full flex-shrink-0" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center text-slate-500 font-bold text-[8px] flex-shrink-0">
                      {displayStoreName ? displayStoreName.substring(0, 2).toUpperCase() : "AL"}
                    </div>
                  )
                )}
                <div>
                  <div className="font-bold text-[11px] leading-tight text-slate-900">
                    {displayStoreName}
                  </div>
                  <div className="text-[7px] text-slate-500 leading-tight max-w-[180px]">
                    {displayStoreAddress}
                  </div>
                  <div className="text-[7px] text-slate-500 leading-tight">
                    {displayStorePhone}
                  </div>
                </div>
              </div>
              <div className="text-[7px] leading-tight space-y-0.5">
                <div className="flex justify-end gap-0.5">
                  <span className="text-slate-500 w-[95px] text-left shrink-0">Nama Pelanggan</span>
                  <span className="text-slate-500 w-[10px] text-center shrink-0">:</span>
                  <span className="font-bold w-[110px] text-left shrink-0">Pelanggan Umum</span>
                </div>
                {isKuliner && (
                  <div className="flex justify-end gap-0.5 font-bold text-slate-900">
                    <span className="text-slate-500 w-[95px] text-left shrink-0">Meja</span>
                    <span className="text-slate-500 w-[10px] text-center shrink-0">:</span>
                    <span className="w-[110px] text-left shrink-0">Meja 04</span>
                  </div>
                )}
                <div className="flex justify-end gap-0.5">
                  <span className="text-slate-500 w-[95px] text-left shrink-0">Nomor Transaksi</span>
                  <span className="text-slate-500 w-[10px] text-center shrink-0">:</span>
                  <span className="font-bold w-[110px] text-left shrink-0">TRX-004</span>
                </div>
                <div className="flex justify-end gap-0.5">
                  <span className="text-slate-500 w-[95px] text-left shrink-0">Tanggal & Waktu</span>
                  <span className="text-slate-500 w-[10px] text-center shrink-0">:</span>
                  <span className="w-[110px] text-left shrink-0">24 Jun 2026, 14.50 WIB</span>
                </div>
                <div className="flex justify-end gap-0.5">
                  <span className="text-slate-500 w-[95px] text-left shrink-0">Tipe Penjualan</span>
                  <span className="text-slate-500 w-[10px] text-center shrink-0">:</span>
                  <span className="w-[110px] text-left shrink-0">Regular</span>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-300 my-1.5" />

            <table className="w-full text-left text-[7px] border-collapse mb-2">
              <thead>
                <tr className="border-b border-slate-300 bg-slate-50 font-bold">
                  <th className="py-1 px-1 w-6 text-center">No</th>
                  <th className="py-1 px-1">Nama Produk</th>
                  <th className="py-1 px-1 text-center w-12">Jumlah</th>
                  <th className="py-1 px-1 text-right w-16">Harga Satuan</th>
                  <th className="py-1 px-1 text-right w-16">Harga Total</th>
                </tr>
              </thead>
              <tbody>
                {isKuliner ? (
                  <>
                    <tr className="border-b border-slate-100">
                      <td className="py-1 px-1 text-center">1</td>
                      <td className="py-1 px-1">
                        <div className="font-medium">Caramel Memo (Iced)</div>
                      </td>
                      <td className="py-1 px-1 text-center">1 Cup</td>
                      <td className="py-1 px-1 text-right">Rp 19.000</td>
                      <td className="py-1 px-1 text-right">Rp 19.000</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-1 px-1 text-center">2</td>
                      <td className="py-1 px-1 font-medium">Choco Caramel (Frappe)</td>
                      <td className="py-1 px-1 text-center">1 Cup</td>
                      <td className="py-1 px-1 text-right">Rp 22.000</td>
                      <td className="py-1 px-1 text-right">Rp 22.000</td>
                    </tr>
                  </>
                ) : (
                  <>
                    <tr className="border-b border-slate-100">
                      <td className="py-1 px-1 text-center">1</td>
                      <td className="py-1 px-1">
                        <div className="font-medium">Kemeja Polos Premium (L)</div>
                        <div className="text-[6.5px] italic text-amber-700">* Warna Biru Dongker</div>
                      </td>
                      <td className="py-1 px-1 text-center">1 Pcs</td>
                      <td className="py-1 px-1 text-right">Rp 125.000</td>
                      <td className="py-1 px-1 text-right">Rp 125.000</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-1 px-1 text-center">2</td>
                      <td className="py-1 px-1 font-medium">Celana Chino Slim Fit (32)</td>
                      <td className="py-1 px-1 text-center">1 Pcs</td>
                      <td className="py-1 px-1 text-right">Rp 175.000</td>
                      <td className="py-1 px-1 text-right">Rp 175.000</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>

            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <p className="font-bold text-[7.5px] mb-0.5">Hormat Kami</p>
                <p className="text-[7px] text-slate-500 mb-1">{footerNote || "Terima Kasih Telah Menjadi Pelanggan Kami"}</p>
              </div>
              <div className="text-[7px] space-y-0.5">
                <div className="flex justify-between">
                  <span>Sub Total</span>
                  <span>: {isKuliner ? "Rp 41.000" : "Rp 300.000"}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>Grand Total</span>
                  <span>: {isKuliner ? "Rp 41.000" : "Rp 300.000"}</span>
                </div>
                <div className="flex justify-between">
                  <span>Metode Pembayaran</span>
                  <span>: Tunai</span>
                </div>
                <div className="flex justify-between">
                  <span>Status Pembayaran</span>
                  <span>: Lunas</span>
                </div>
              </div>
            </div>

            <div className="text-center text-[6.5px] text-slate-400 mt-2">
              Powered by ASAlink
            </div>
          </div>
        )}
      </div>

      <p className="text-[11px] text-slate-400 mt-3 text-center">
        Preview ini hanya simulasi tampilan, bukan nota asli.
      </p>
    
      {/* Modal Petunjuk Instalasi Helper Windows */}
    </div>
  );
}
